/**
 * LAS SECCIONES DE PREGUNTAS DEL MENSUAL (doc «Reporte mensual plan personalizado», 25-10, con
 * el rediseño del 28-10).
 *
 * Qué secciones salen, sus preguntas y sus palabras las manda el servidor
 * (`ficha.secciones_mensual`, de `core/mensual.SECCIONES`): aquí solo se pintan. UN SOLO
 * DISEÑO PARA CADA TIPO DE PREGUNTA (28-10: «mantén una consistencia»):
 *
 *   grado      «¿En qué grado has cumplido...?»: SOLO si esta semana no hay check-in (o no hay
 *              contra qué medirlo). Con check-in sale de lo apuntado y no se enseña.
 *   estrellas  de 1 a 5, con lo que significan los dos extremos debajo. Las que llevan
 *              `salvo` se callan si a la de su `salvo` le puso esa nota (una estrella).
 *   opciones   una sola, en botones a lo ancho; la marcada en naranja con texto negro. Las
 *              que llevan `sigue` le pasan a la siguiente sección al tocarlas.
 *   objetivo   «Mismo objetivo» (sigue) o «Quiero cambiar», y con el cambio, a cuál: los
 *              del cuestionario del alta menos el que ya tiene; al elegirlo, sigue.
 *   si_no      «No» o «Sí» en píldora, y con el sí, la casilla para contarlo.
 *
 * «Tu material» lleva además sus dos listas (maqueta 2): los ejercicios que no puede hacer y
 * su maquinaria (la básica que no tiene y la otra, menos habitual, que sí). Sin «Confirmar»
 * (28-10): pulsar «Continuar» ya es confirmarlas como están. Las secciones `automatica` no
 * llevan «Continuar»: pasan solas en cuanto están enteras. Las respuestas van a `valores.respuestas_mensual` (`lib/revisarReporte`).
 */
import React, { useEffect, useState } from 'react';
import { CasillaLibre, Estrellas, ListaDeEtiquetas } from './piezas';
import { seCalla, seLeEnsena } from '../../lib/revisarReporte';
import { useAuth } from '../../context/AuthContext';
import { objetivosDelCuestionario, nombreDelObjetivo, normalizarObjetivo } from '../../lib/objetivos';

const ORANGE = '#FF671F';

/** La tarjeta de una pregunta. `destacada`, con el borde naranja de las maquetas. */
const Tarjeta = ({ children, testid, destacada = false }) => (
    <div className="rounded-2xl bg-card border p-4 space-y-3" data-testid={testid}
        style={{ borderColor: destacada ? ORANGE : undefined }}>
        {children}
    </div>
);

const Pregunta = ({ p, texto }) => (
    <div>
        <p className="text-base font-bold text-foreground">{texto || p.pregunta}</p>
        {p?.ayuda && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{p.ayuda}</p>}
    </div>
);

/** Una sola de varias, en botones a lo ancho. */
export const Eleccion = ({ opciones, valor, onChange, testid }) => (
    <div className="grid gap-2" data-testid={testid}>
        {opciones.map((o) => {
            const activo = valor === o.valor;
            return (
                <button key={o.valor} type="button" onClick={() => onChange(o.valor)}
                    data-testid={`${testid}-${o.valor}`} aria-pressed={activo}
                    className={`w-full rounded-xl border px-3.5 py-3 text-left text-sm font-semibold leading-snug transition-colors ${
                        activo ? 'border-transparent text-black' : 'border-border text-foreground/80 hover:border-foreground/30'}`}
                    style={activo ? { backgroundColor: ORANGE } : undefined}>
                    {o.texto}
                </button>
            );
        })}
    </div>
);

/** «No» y «Sí», en píldora. */
const Pastilla = ({ activo, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={activo}
        className={`rounded-full px-5 py-2 text-sm font-bold border transition-colors ${
            activo ? 'border-transparent text-black' : 'border-border text-foreground/80 hover:border-foreground/30'}`}
        style={activo ? { backgroundColor: ORANGE } : undefined}>
        {children}
    </button>
);

/** Los suyos, para «Señala qué quieres que te quite»: los de su pauta de hoy. */
const useSusSuplementos = (api, activo) => {
    const [nombres, setNombres] = useState(null);
    useEffect(() => {
        if (!activo || nombres) return;
        api.get('/supplements/current')
            .then((r) => setNombres([...new Set((r.data?.actual || []).map((x) => x.titulo).filter(Boolean))]))
            .catch((e) => { console.error('No se pudo traer su suplementación', e); setNombres([]); });
    }, [api, activo, nombres]);
    return nombres;
};

/**
 * @param onSigue (valoresNuevos) => …: pasa a la siguiente sección si esta ya está entera
 *                con esos valores. Lo llaman las opciones con `sigue` y el objetivo elegido.
 */
const MensualSeccion = ({ api, seccion, valores, set, grados, onSigue }) => {
    const { profile } = useAuth();
    const r = valores.respuestas_mensual || {};
    const conRespuesta = (clave, v) => ({ ...valores, respuestas_mensual: { ...r, [clave]: v } });
    const poner = (clave, v) => set('respuestas_mensual', { ...r, [clave]: v });
    const quitar = (seccion.preguntas || []).find((p) => p.quitar);
    const susSuplementos = useSusSuplementos(api, !!quitar && r[quitar.clave] === quitar.quitar.si);
    // Tras tocar, un instante para que se vea la marcada antes de pasar.
    const seguir = (nuevos) => { if (onSigue) setTimeout(() => onSigue(nuevos), 250); };
    // EN LAS SECCIONES SIN «CONTINUAR» (`automatica`, 28-10) cada respuesta mira si la sección
    // ya está entera y, si lo está, pasa a la siguiente.
    const responder = (clave, v) => {
        poner(clave, v);
        if (seccion.automatica) seguir(conRespuesta(clave, v));
    };

    const visibles = (seccion.preguntas || []).filter((p) =>
        !(p.tipo === 'grado' && seLeEnsena(p, grados)) && !seCalla(p, valores));

    const pintar = (p, i) => {
        const testid = `mensual-${p.clave}`;
        const destacada = i === 0;
        if (p.tipo === 'grado' || p.tipo === 'estrellas') {
            return (
                <Tarjeta key={p.clave} testid={testid} destacada={destacada}>
                    <Pregunta p={p} />
                    <Estrellas testid={`${testid}-estrellas`} valor={r[p.clave]}
                        onChange={(v) => responder(p.clave, v)} minLabel={p.minimo} maxLabel={p.maximo} />
                </Tarjeta>
            );
        }
        if (p.tipo === 'objetivo') {
            const cambia = r[p.clave] === 'cambiar';
            const actual = normalizarObjetivo(valores.objetivo_actual);
            const posibles = objetivosDelCuestionario(profile?.sex).filter((o) => o.clave !== actual);
            return (
                <React.Fragment key={p.clave}>
                    <Tarjeta testid={testid} destacada={destacada}>
                        <Pregunta p={p} />
                        {actual && (
                            <p className="text-sm text-muted-foreground" data-testid="mensual-objetivo-actual">
                                Tu objetivo actual: <b className="text-foreground">{nombreDelObjetivo(actual)}</b>
                            </p>
                        )}
                        <Eleccion testid={testid} opciones={p.opciones} valor={r[p.clave]}
                            onChange={(v) => {
                                const nuevos = { ...conRespuesta(p.clave, v), proximo_objetivo: '' };
                                // Con «Mismo objetivo» no se propone ninguno: el entrenador solo
                                // se entera de un cambio que se pide.
                                set('respuestas_mensual', nuevos.respuestas_mensual);
                                set('proximo_objetivo', '');
                                if ((p.opciones.find((o) => o.valor === v) || {}).sigue) seguir(nuevos);
                            }} />
                    </Tarjeta>
                    {cambia && (
                        <Tarjeta testid="mensual-proximo-objetivo">
                            <Pregunta texto={p.pregunta_si_cambia} />
                            <Eleccion testid="mensual-proximo-objetivo"
                                opciones={posibles.map((o) => ({ valor: o.clave, texto: o.texto }))}
                                valor={valores.proximo_objetivo}
                                onChange={(v) => {
                                    set('proximo_objetivo', v);
                                    seguir({ ...valores, proximo_objetivo: v });
                                }} />
                        </Tarjeta>
                    )}
                </React.Fragment>
            );
        }
        if (p.tipo === 'opciones') {
            const conQuitar = p.quitar && r[p.clave] === p.quitar.si;
            const lista = r[`${p.clave}_quitar`] || [];
            return (
                <Tarjeta key={p.clave} testid={`${testid}-bloque`} destacada={destacada}>
                    <Pregunta p={p} />
                    <Eleccion testid={testid} opciones={p.opciones} valor={r[p.clave]}
                        onChange={(v) => {
                            if (seccion.automatica) { responder(p.clave, v); return; }
                            poner(p.clave, v);
                            if ((p.opciones.find((o) => o.valor === v) || {}).sigue) seguir(conRespuesta(p.clave, v));
                        }} />
                    {conQuitar && (
                        <div className="border-l-2 pl-3 space-y-2" style={{ borderColor: ORANGE }}>
                            <p className="text-base font-bold text-foreground">{p.quitar.pregunta}</p>
                            {susSuplementos && susSuplementos.length ? (
                                <div className="flex flex-wrap gap-2">
                                    {susSuplementos.map((n) => {
                                        const puesto = lista.includes(n);
                                        return (
                                            <Pastilla key={n} activo={puesto} testid={`mensual-quitar-${n}`}
                                                onClick={() => poner(`${p.clave}_quitar`,
                                                    puesto ? lista.filter((x) => x !== n) : [...lista, n])}>
                                                {puesto ? `${n} ×` : n}
                                            </Pastilla>
                                        );
                                    })}
                                </div>
                            ) : susSuplementos && (
                                <ListaDeEtiquetas testid="mensual-quitar" lista={lista} redondas
                                    onChange={(v) => poner(`${p.clave}_quitar`, v)}
                                    ejemplo="Cafeína" anadir="+ Añadir suplemento" />
                            )}
                        </div>
                    )}
                </Tarjeta>
            );
        }
        if (p.tipo === 'si_no') {
            const si = r[p.clave] === 'si';
            return (
                <Tarjeta key={p.clave} testid={testid} destacada={destacada}>
                    <Pregunta p={p} />
                    <div className="flex flex-wrap gap-2">
                        <Pastilla activo={r[p.clave] === 'no'} onClick={() => poner(p.clave, 'no')} testid={`${testid}-no`}>No</Pastilla>
                        <Pastilla activo={si} onClick={() => poner(p.clave, 'si')} testid={`${testid}-si`}>Sí</Pastilla>
                    </div>
                    {si && (
                        <CasillaLibre valor={r[`${p.clave}_detalle`] || ''} placeholder={p.campo}
                            onChange={(v) => poner(`${p.clave}_detalle`, v)} testid={`${testid}-detalle`} />
                    )}
                </Tarjeta>
            );
        }
        return null;
    };

    return (
        <div className="space-y-3" data-testid={`mensual-seccion-${seccion.clave}`}>
            {visibles.map(pintar)}

            {seccion.clave === 'material' && (
                <>
                    {seccion.ejercicios && (
                        <Tarjeta testid="mensual-ejercicios" destacada={!visibles.length}>
                            <div>
                                <p className="text-base font-bold text-foreground">Ejercicios que no puedes hacer</p>
                                <p className="text-sm text-muted-foreground mt-1">Estos son los que no pudiste hacer el mes pasado.</p>
                            </div>
                            <ListaDeEtiquetas testid="molestias" lista={valores.ejercicios_molestos || []} redondas
                                onChange={(v) => set('ejercicios_molestos', v)}
                                ejemplo="Press militar" anadir="+ Añadir" />
                        </Tarjeta>
                    )}
                    <Tarjeta testid="mensual-maquinaria" destacada={!visibles.length && !seccion.ejercicios}>
                        <p className="text-base font-bold text-foreground">Maquinaria básica que no tienes</p>
                        <ListaDeEtiquetas testid="maquinas" lista={valores.maquinas_no_disponibles || []} redondas
                            onChange={(v) => set('maquinas_no_disponibles', v)}
                            ejemplo="Prensa" anadir="+ Añadir" />
                        <div className="pt-1">
                            <p className="text-base font-bold text-foreground">Otra maquinaria</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Menos habituales, pero muy buenas y que merece la pena incluir.
                            </p>
                        </div>
                        <ListaDeEtiquetas testid="maquinas-especiales" lista={valores.maquinas_especiales || []} redondas
                            onChange={(v) => set('maquinas_especiales', v)}
                            ejemplo="Hack" anadir="+ Añadir" />
                    </Tarjeta>
                </>
            )}
        </div>
    );
};

export default MensualSeccion;
